//PublicView.js

import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import toast, { Toaster } from 'react-hot-toast';
import {
MapPin, Menu, X, User, ChevronDown, Plus, Minus,
Instagram, ChevronRight, ChevronLeft, Star, Clock, Bike, Package, Loader2,
Mail, Info, Globe, MessageSquare, Send, LogOut, Store, Paperclip, Mic, Trash2, QrCode,
LayoutGrid, List, Search
} from 'lucide-react';
import { io } from 'socket.io-client';
import EnviosApp from './EnviosApp';
import ClienteArea from './ClienteArea';
import '../styles/PublicView.css';
import RestauranteDesativado from '../templates/RestauranteDesativado';

export default function PublicView({ forceView }) {
const { slug } = useParams();

// --- ESTADOS ---
const [dados, setDados] = useState(null);
const [loading, setLoading] = useState(true);
const [isInactive, setIsInactive] = useState(false);
const [isMenuOpen, setIsMenuOpen] = useState(false);
const [viewMode, setViewMode] = useState(forceView === 'cliente' ? 'cliente' : 'menu');
const [layoutStyle, setLayoutStyle] = useState('list');
const [cart, setCart] = useState([]);
const [showCartModal, setShowCartModal] = useState(false);
const [step, setStep] = useState('cart');
const [chatForm, setChatForm] = useState({ nome: '', whatsapp: '', email: '', cpf: '' });
const [isChatAuth, setIsChatAuth] = useState(false);
const [webchatSettings, setWebchatSettings] = useState(null);
const [showChat, setShowChat] = useState(false);
const [chatMsg, setChatMsg] = useState("");
const [chatHistory, setChatHistory] = useState(() => {
const salvo = localStorage.getItem('chat_history_cache');
return salvo ? JSON.parse(salvo) : [{ from: 'bot', text: 'Olá! Como posso ajudar você hoje?' }];
});
const [mostrarQRCode, setMostrarQRCode] = useState(false);
const [hoveredMsgId, setHoveredMsgId] = useState(null);

// Banners, Alertas e Pesquisa
const [bannersList, setBannersList] = useState([]);
const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
const [alertasList, setAlertasList] = useState([]);
const [showAlertModal, setShowAlertModal] = useState(false);
const [currentAlertIndex, setCurrentAlertIndex] = useState(0);
const [searchTerm, setSearchTerm] = useState('');

// Estados para Upload/Gravação
const [fileToUpload, setFileToUpload] = useState(null);
const [filePreviewUrl, setFilePreviewUrl] = useState(null);
const [isRecordingWebchat, setIsRecordingWebchat] = useState(false);
const [recordingTimeWebchat, setRecordingTimeWebchat] = useState(0);
const [allowUpload, setAllowUpload] = useState(true);

const [selectedProduct, setSelectedProduct] = useState(null);
const [quantidade, setQuantidade] = useState(1);
const [selecaoGrupos, setSelecaoGrupos] = useState({});
const [observacao, setObservacao] = useState('');
const [taxasEntrega, setTaxasEntrega] = useState({ metodo: 'raio', faixas: [] });
const [taxaEntregaSoma, setTaxaEntregaSoma] = useState(0);
const [cepCliente, setCepCliente] = useState('');
const [cepCalculado, setCepCalculado] = useState(false);
const [clienteLogado, setClienteLogado] = useState(null);
const [pedidosCliente, setPedidosCliente] = useState([]);

const chatEndRef = useRef(null);
const sliderRef = useRef(null);
const categorySlidersRef = useRef({});
const fileInputRef = useRef(null);
const mediaRecorderRef = useRef(null);
const audioChunksRef = useRef([]);
const recordTimerRef = useRef(null);

// --- MEMOS BLINDADOS (Evitam erro #310 de Hooks/Render) ---
const categorias = useMemo(() => dados?.categorias || [], [dados?.categorias]);
const itensCardapio = useMemo(() => dados?.itens || [], [dados?.itens]);
const totalCart = useMemo(() => cart.reduce((acc, item) => acc + item.precoTotal, 0), [cart]);
const itensDestaque = useMemo(() => itensCardapio.filter(i => Number(i.destaque) === 1), [itensCardapio]);

const precoFinalExibicao = useMemo(() => {
if (!selectedProduct) return 0;
let basePrice = parseFloat(selectedProduct.preco || 0);
let totalAdicionais = 0;

Object.keys(selecaoGrupos).forEach(grupoId => {
const grupoInfo = (selectedProduct.adicionais || []).find(g => g.id === parseInt(grupoId));
const selecionados = selecaoGrupos[grupoId] || [];

if (grupoInfo?.metodo_preco === 'substitui') {
 if (selecionados.length > 0) basePrice = parseFloat(selecionados[0].preco || 0);
} else if (grupoInfo?.metodo_preco === 'maior_valor') {
 totalAdicionais += Math.max(...selecionados.map(it => parseFloat(it.preco || 0)), 0);
} else {
 selecionados.forEach(it => totalAdicionais += parseFloat(it.preco || 0));
}
});
return (basePrice + totalAdicionais) * quantidade;
}, [selectedProduct, selecaoGrupos, quantidade]);

// Filtro global de itens
const filteredCategorias = useMemo(() => {
if (!searchTerm.trim()) return categorias;
const term = searchTerm.toLowerCase();
return categorias.filter(cat => {
const itensCat = itensCardapio.filter(i => Number(i.categoria_id) === Number(cat.id));
const matchesCat = cat.nome.toLowerCase().includes(term);
const matchesItem = itensCat.some(i => i.nome.toLowerCase().includes(term) || (i.descricao && i.descricao.toLowerCase().includes(term)));
return matchesCat || matchesItem;
});
}, [categorias, itensCardapio, searchTerm]);

// --- EFEITOS ---
useEffect(() => {
if (bannersList.length > 1) {
const interval = setInterval(() => {
 setCurrentBannerIndex((prev) => (prev + 1) % bannersList.length);
}, 4000);
return () => clearInterval(interval);
}
}, [bannersList.length]);

useEffect(() => {
localStorage.setItem('chat_history_cache', JSON.stringify(chatHistory));
}, [chatHistory]);

const scrollToBottomChat = () => chatEndRef.current?.scrollIntoView({ behavior: "smooth" });

useEffect(() => {
scrollToBottomChat();
}, [chatHistory, showChat]);

useEffect(() => {
let isMounted = true;

const fetchData = async () => {
try {
 setLoading(true);
 const res = await api.get(`/cardapio/view/${slug}`);

 if (!isMounted) return;

 const rawData = res.data;

 if (Number(rawData.estabelecimento.ativo) === 0) {
 setIsInactive(true);
 setLoading(false);
 return;
 }

 try {
 const resBanners = await api.get(`/marketing/${rawData.estabelecimento.id}/banners`);
 if (isMounted) setBannersList(resBanners.data || []);
 } catch (e) {
 console.error("Erro ao buscar banners:", e);
 }

 try {
 const resAlertas = await api.get(`/marketing/${rawData.estabelecimento.id}/alertas`);
 if (isMounted && resAlertas.data && resAlertas.data.length > 0) {
 const agora = new Date();
 
 const ativos = resAlertas.data.filter(alerta => {
 if (Number(alerta.ativo) !== 1) return false;

 if (alerta.data_inicio) {
  const inicio = new Date(alerta.data_inicio.includes(' ') ? alerta.data_inicio.replace(' ', 'T') : alerta.data_inicio);
  if (!isNaN(inicio.getTime()) && agora < inicio) return false;
 }

 if (alerta.data_fim) {
  const fim = new Date(alerta.data_fim.includes(' ') ? alerta.data_fim.replace(' ', 'T') : alerta.data_fim);
  if (!isNaN(fim.getTime()) && agora > fim) return false;
 }

 return true;
 });

 const alertasParaMostrar = ativos.filter(alerta => !localStorage.getItem(`hide_alert_${alerta.id}`));
 if (alertasParaMostrar.length > 0) {
 setAlertasList(alertasParaMostrar);
 setShowAlertModal(true);
 }
 }
 } catch (e) {
 console.error("Erro ao buscar alertas:", e);
 }

 let chatConfig = { primaryColor: rawData.estabelecimento.cor_primaria || '#ea1d2c', title: 'Atendimento', fields: [] };
 if (rawData.estabelecimento.webchat_config) {
 try {
 const parsed = typeof rawData.estabelecimento.webchat_config === 'string'
 ? JSON.parse(rawData.estabelecimento.webchat_config)
 : rawData.estabelecimento.webchat_config;
 chatConfig = { ...chatConfig, ...parsed };
 } catch (e) { console.error("Erro no parse do webchat", e); }
 }
 setWebchatSettings(chatConfig);
 setAllowUpload(chatConfig.enableUpload !== false);

 setDados({
 ...rawData,
 categorias: (rawData.categorias || []).sort((a, b) => Number(a.ordem || 0) - Number(b.ordem || 0)),
 itens: (rawData.itens || []).sort((a, b) => Number(a.ordem || 0) - Number(b.ordem || 0))
 });

 const resTaxas = await api.get(`/cardapio/estabelecimentos/${rawData.estabelecimento.id}/config-entrega-completa`);
 if (isMounted) {
 setTaxasEntrega({
 metodo: resTaxas.data.metodo,
 faixas: resTaxas.data.metodo === 'raio' ? resTaxas.data.raio : resTaxas.data.bairro
 });
 }
} catch (err) {
 if (!isMounted) return;
 if (err.response?.status === 403 || err.response?.data?.status === "inativo") {
 setIsInactive(true);
 } else {
 toast.error("Erro ao carregar cardápio.");
 }
} finally {
 if (isMounted) setLoading(false);
}
};

fetchData();
return () => { isMounted = false; };
}, [slug]);

// --- QR CODE URL PARAM PARSER (Adiciona direto na sacola e abre o checkout) ---
useEffect(() => {
if (!dados || !dados.itens) return;

const params = new URLSearchParams(window.location.search);
const produtoIdUrl = params.get('produto');

if (produtoIdUrl) {
const produtoEncontrado = dados.itens.find(i => Number(i.id) === Number(produtoIdUrl));

if (produtoEncontrado) {
 const novoItemCart = {
 ...produtoEncontrado,
 tempId: Date.now(),
 quantidade: 1,
 precoTotal: parseFloat(produtoEncontrado.preco || 0),
 escolhas: {},
 observacao: 'Adicionado via QR Code'
 };

 setCart(prev => [...prev, novoItemCart]);
 setStep('cart');
 setShowCartModal(true);

 window.history.replaceState({}, document.title, window.location.pathname);
 toast.success(`${produtoEncontrado.nome} adicionado à sacola!`);
}
}
}, [dados]);

useEffect(() => {
setViewMode(forceView === 'cliente' ? 'cliente' : 'menu');
}, [forceView]);

useEffect(() => {
if (cart.length === 0) {
setTaxaEntregaSoma(0);
setCepCliente('');
setCepCalculado(false);
}
}, [cart]);

useEffect(() => {
const clienteSalvo = localStorage.getItem('clienteLogado');
const isAuthManual = localStorage.getItem('webchat_authenticated') === 'true';
const savedUserInfo = localStorage.getItem('webchat_user_info');

if (savedUserInfo) {
try {
 setChatForm(JSON.parse(savedUserInfo));
} catch(e) {}
}

if (clienteSalvo) {
try {
 const clienteParsed = JSON.parse(clienteSalvo);
 setClienteLogado(clienteParsed);

 setIsChatAuth(true);
 setChatForm({
 nome: clienteParsed.nome || '',
 whatsapp: clienteParsed.whatsapp || '',
 email: clienteParsed.email || '',
 cpf: clienteParsed.cpf || ''
 });
} catch (e) {
 localStorage.removeItem('clienteLogado');
}
}

if (webchatSettings) {
const camposAtivos = webchatSettings.fields?.filter(f => f.active === true) || [];

if (clienteSalvo || isAuthManual || camposAtivos.length === 0) {
 setIsChatAuth(true);
} else {
 setIsChatAuth(false);
}
}
}, [webchatSettings]);

useEffect(() => {
if (!dados?.estabelecimento?.id || dados.estabelecimento.webchat_ativo !== 1) return;

let webchatId = localStorage.getItem('webchat_id');
if (!webchatId) {
webchatId = `web_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
localStorage.setItem('webchat_id', webchatId);
}

const socket = io('https://app.universomidiadigital.com.br', {
transports: ['websocket', 'polling'],
reconnection: true
});

socket.on('connect', () => {
socket.emit('join-visitor', webchatId);
socket.emit('join', dados.estabelecimento.id);
});

socket.on('toggle-upload-webchat', (data) => {
if (data && data.allow !== undefined) {
 setAllowUpload(data.allow);
 if (!data.allow) {
 setFileToUpload(null);
 setFilePreviewUrl(null);
 }
}
});

socket.on('webchat-response', (msg) => {
if (!msg) return;
setChatHistory(prev => {
 const exists = prev.some(m => (m.msg_id && m.msg_id === msg.msg_id) || (m.timestamp === msg.timestamp && m.text === msg.text));
 if (exists) return prev;

 let finalMediaUrl = msg.mediaUrl || msg.fileUrl || msg.url || msg.image || msg.document || msg.audio;

 if (!finalMediaUrl && msg.message) {
 finalMediaUrl = msg.message?.imageMessage?.url ||
  msg.message?.documentMessage?.url ||
  msg.message?.videoMessage?.url ||
  msg.message?.audioMessage?.url;
 }

 if (!finalMediaUrl && msg.base64) {
 const mime = msg.mimetype || msg.mediaType || msg.type || 'image/jpeg';
 finalMediaUrl = `data:${mime};base64,${msg.base64}`;
 }

 return [...prev, {
 msg_id: msg.msg_id,
 from: 'bot',
 text: msg.text || '',
 mediaUrl: finalMediaUrl,
 mediaType: msg.mediaType || msg.mimetype || msg.type || '',
 timestamp: msg.timestamp,
 operatorName: msg.operatorName,
 reaction: msg.reaction
 }];
});
setShowChat(true);
});

socket.on('webchat-reaction', (data) => {
setChatHistory(prev => prev.map(m =>
 m.msg_id === data.msg_id ? { ...m, reaction: data.reaction } : m
));
});

return () => {
socket.off('webchat-response');
socket.off('toggle-upload-webchat');
socket.off('webchat-reaction');
socket.disconnect();
};
}, [dados?.estabelecimento?.id]);

const handleCloseAlert = () => {
if (currentAlertIndex < alertasList.length - 1) {
setCurrentAlertIndex(prev => prev + 1);
} else {
setShowAlertModal(false);
}
};

const handleDontShowAgain = (alertaId) => {
localStorage.setItem(`hide_alert_${alertaId}`, 'true');
handleCloseAlert();
};

// --- RENDERIZAÇÃO CONDICIONAL (APÓS TODOS OS HOOKS) ---
if (loading) return <div className="loader-full">Carregando Universo Mídia...</div>;
if (isInactive) return <RestauranteDesativado />;
if (!dados?.estabelecimento) return <div className="loader-full">Cardápio não encontrado.</div>;

const { estabelecimento } = dados;
const corPrimaria = estabelecimento.cor_primaria || '#ea1d2c';
const banner = estabelecimento.banner_url || 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?q=80&w=1000';
const logo = estabelecimento.logo_url || '/assets/logo-default.png';
const tipo = estabelecimento.layout_tipo || 'padrao';
const aberto = (new Date().getHours() >= (parseInt(estabelecimento.horario_abertura) || 0) && new Date().getHours() < (parseInt(estabelecimento.horario_fechamento) || 23));

const headerStyle = {
backgroundImage: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.85)), url(${banner})`,
backgroundSize: 'cover',
backgroundPosition: 'center'
};

const handleLoginToken = async (codigoDigitado, email) => {
setLoading(true);
try {
const res = await api.post('/clientes/login-token', { token: codigoDigitado, email });

if (res.data.success) {
 const tokenParaSalvar = codigoDigitado;
 const clienteValido = res.data.cliente;

 localStorage.setItem('clienteLogado', JSON.stringify(clienteValido));
 localStorage.setItem('token_acesso', tokenParaSalvar);

 setClienteLogado(clienteValido);

 const resPedidos = await api.get(`/clientes/pedidos/${clienteValido.cpf}?token=${tokenParaSalvar}`);
 setPedidosCliente(resPedidos.data.pedidos || []);

 toast.success(`Bem-vindo, ${clienteValido.nome}!`);
 setViewMode('cliente');
}
} catch (err) {
toast.error("Código ou e-mail inválido.");
} finally {
setLoading(false);
}
};

const formatTime = (seconds) => {
const mins = Math.floor(seconds / 60);
const secs = seconds % 60;
return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const handleFileSelect = (e) => {
const file = e.target.files[0];
if (!file) return;
setFileToUpload(file);
if (file.type.startsWith('image/')) {
setFilePreviewUrl(URL.createObjectURL(file));
} else if (file.type.startsWith('audio/')) {
setFilePreviewUrl('audio');
} else {
setFilePreviewUrl('doc');
}
};

const startRecordingWebchat = async () => {
try {
const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
mediaRecorderRef.current = new MediaRecorder(stream);
audioChunksRef.current = [];

mediaRecorderRef.current.ondataavailable = (e) => {
 if (e.data.size > 0) audioChunksRef.current.push(e.data);
};

mediaRecorderRef.current.onstop = () => {
 const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/ogg; codecs=opus' });
 const file = new File([audioBlob], `voice_${Date.now()}.ogg`, { type: 'audio/ogg' });
 setFileToUpload(file);
 setFilePreviewUrl('audio');
 stream.getTracks().forEach(track => track.stop());
};

mediaRecorderRef.current.start();
setIsRecordingWebchat(true);
setRecordingTimeWebchat(0);
recordTimerRef.current = setInterval(() => {
 setRecordingTimeWebchat(prev => prev + 1);
}, 1000);
} catch (err) {
console.error("Erro ao acessar microfone", err);
toast.error("Permissão de microfone negada.");
}
};

const stopRecordingWebchat = () => {
if (mediaRecorderRef.current && isRecordingWebchat) {
mediaRecorderRef.current.stop();
setIsRecordingWebchat(false);
clearInterval(recordTimerRef.current);
}
};

const cancelRecordingWebchat = () => {
if (mediaRecorderRef.current && isRecordingWebchat) {
mediaRecorderRef.current.stop();
audioChunksRef.current = [];
setFileToUpload(null);
setFilePreviewUrl(null);
setIsRecordingWebchat(false);
clearInterval(recordTimerRef.current);
}
};

const handleSendWebchat = async (e) => {
if (e) e.preventDefault();
if (!chatMsg.trim() && !fileToUpload) return;

let webchatId = localStorage.getItem('webchat_id');
if (!webchatId) {
webchatId = `web_${Date.now()}`;
localStorage.setItem('webchat_id', webchatId);
}

const tempMsgId = `temp_${Date.now()}`;
const localMsgText = fileToUpload && !chatMsg.trim() ? "Enviou uma mídia" : chatMsg;
const mimeTypeOrigem = fileToUpload ? fileToUpload.type : null;

const userMsg = {
msg_id: tempMsgId,
from: 'me',
text: localMsgText,
mediaUrl: filePreviewUrl && filePreviewUrl !== 'audio' && filePreviewUrl !== 'doc' ? filePreviewUrl : null,
mediaType: mimeTypeOrigem,
timestamp: Date.now()
};

setChatHistory(prev => [...prev, userMsg]);
const msgToSend = chatMsg;
const fileToSend = fileToUpload;

setChatMsg("");
setFileToUpload(null);
setFilePreviewUrl(null);

try {
const contactName = clienteLogado?.nome || chatForm.nome || "Visitante Site";
let res;

if (fileToSend) {
 const formData = new FormData();
 formData.append('sessionId', webchatId);
 formData.append('contactName', contactName);
 if (msgToSend) formData.append('mensagem', msgToSend);
 formData.append('media', fileToSend);

 res = await api.post(`/whatsapp/webchat/send/${dados.estabelecimento.id}`, formData);
} else {
 res = await api.post(`/whatsapp/webchat/send/${dados.estabelecimento.id}`, {
 sessionId: webchatId,
 mensagem: msgToSend,
 contactName
 });
}

if (res.data && res.data.msg_id) {
 setChatHistory(prev => prev.map(m =>
 m.msg_id === tempMsgId ? {
 ...m,
 msg_id: res.data.msg_id,
 mediaUrl: res.data.mediaUrl || res.data.url || m.mediaUrl,
 mediaType: res.data.mediaType || res.data.mimetype || m.mediaType
 } : m
 ));
}
} catch (err) {
console.error("Erro ao enviar:", err);
toast.error("Erro ao enviar mensagem");
setChatHistory(prev => prev.filter(m => m.msg_id !== tempMsgId));
setChatMsg(msgToSend);
setFileToUpload(fileToSend);
}
};

const renderMedia = (m) => {
if (!m.mediaUrl) return null;

let url = m.mediaUrl;
if (!url.startsWith('http') && !url.startsWith('blob:') && !url.startsWith('data:')) {
url = url.startsWith('/') ? `https://app.universomidiadigital.com.br${url}` : `https://app.universomidiadigital.com.br/${url}`;
}

const urlWithoutQuery = url.split('?')[0];
const ext = urlWithoutQuery.split('.').pop().toLowerCase();
const typeHint = (m.mediaType || m.mimetype || m.type || '').toLowerCase();

const isAudio = typeHint.includes('audio') || url.startsWith('data:audio') || ['mp3', 'ogg', 'wav', 'm4a', 'opus'].includes(ext) || url.includes('voice_');
const isVideo = typeHint.includes('video') || url.startsWith('data:video') || ['mp4', 'mov', 'avi', 'webm'].includes(ext);
const isExplicitDoc = typeHint.includes('pdf') || typeHint.includes('document') || ['pdf', 'doc', 'docx', 'xls'].includes(ext);

if (isAudio) return <audio controls style={{maxWidth: '200px', height: '40px', marginBottom: '5px'}}><source src={url} /></audio>;
if (isVideo) return <video controls style={{maxWidth: '100%', borderRadius: '8px', marginBottom: '5px'}}><source src={url} /></video>;

if (isExplicitDoc) {
return (
 <a href={url} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '8px', background: m.from === 'me' ? 'rgba(255,255,255,0.2)' : '#e2e8f0', padding: '10px 14px', borderRadius: '8px', color: 'inherit', textDecoration: 'none', marginBottom: '5px', fontSize: '13px' }}>
 <Paperclip size={18} opacity={0.8} />
 <span style={{ textDecoration: 'underline', fontWeight: '500' }}>Abrir Documento</span>
 </a>
);
}

return (
<div className="media-container" style={{ position: 'relative' }}>
 <img
 src={url}
 alt="Mídia"
 style={{maxWidth: '100%', borderRadius: '8px', marginBottom: '5px', maxHeight: '200px', objectFit: 'cover'}}
 onError={(e) => {
 e.target.style.display = 'none';
 if (e.target.nextElementSibling) {
  e.target.nextElementSibling.style.display = 'flex';
 }
 }}
 />
 <a href={url} target="_blank" rel="noreferrer" className="doc-fallback" style={{ display: 'none', alignItems: 'center', gap: '8px', background: m.from === 'me' ? 'rgba(255,255,255,0.2)' : '#e2e8f0', padding: '10px 14px', borderRadius: '8px', color: 'inherit', textDecoration: 'none', marginBottom: '5px', fontSize: '13px' }}>
 <Paperclip size={18} opacity={0.8} />
 <span style={{ textDecoration: 'underline', fontWeight: '500' }}>Ver Arquivo Anexado</span>
 </a>
</div>
);
};

const handleLogout = () => {
localStorage.removeItem('clienteLogado');
localStorage.removeItem('token_acesso');
localStorage.removeItem('webchat_id');
localStorage.removeItem('chat_history_cache');
localStorage.removeItem('webchat_authenticated');
localStorage.removeItem('webchat_user_info');
setClienteLogado(null);
setPedidosCliente([]);
setIsChatAuth(false);
setChatHistory([{ from: 'bot', text: 'Olá! Como posso ajudar você hoje?' }]);
setViewMode('menu');
toast.success("Você saiu da conta.");
};

const handleStartChat = async (e) => {
e.preventDefault();

const nomeFinal = chatForm.nome || chatForm.name || chatForm.Nome || '';
const whatsappFinal = chatForm.whatsapp || chatForm.phone || chatForm.telefone || '';
const emailFinal = chatForm.email || chatForm.Email || '';
const cpfFinal = chatForm.cpf || chatForm.documento || '';

if (webchatSettings?.fields) {
const camposObrigatorios = webchatSettings.fields.filter(f => f.active && f.required);
for (const campo of camposObrigatorios) {
 if (!chatForm[campo.id] || String(chatForm[campo.id]).trim() === "") {
 return toast.error(`O campo ${campo.label} é obrigatório!`);
 }
}
} else {
if (!nomeFinal.trim() || !whatsappFinal.trim()) {
 return toast.error("Por favor, preencha nome e whatsapp.");
}
}

let webchatId = localStorage.getItem('webchat_id');
if (!webchatId) {
webchatId = `web_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
localStorage.setItem('webchat_id', webchatId);
}

const loadingChat = toast.loading("Iniciando atendimento...");
try {
await api.post(`/whatsapp/admin/${dados.estabelecimento.id}/add-contact`, {
 nome: nomeFinal,
 whatsapp: whatsappFinal,
 email: emailFinal,
 cpf: cpfFinal,
 remoteJidOriginal: webchatId,
 channel: 'webchat',
 isWebChat: true
});

localStorage.setItem('webchat_authenticated', 'true');
localStorage.setItem('webchat_user_info', JSON.stringify({ nome: nomeFinal, whatsapp: whatsappFinal, email: emailFinal, cpf: cpfFinal }));

setIsChatAuth(true);
toast.dismiss(loadingChat);
setChatHistory(prev => [...prev, {
 from: 'bot',
 text: `Prazer em te conhecer, ${nomeFinal}! Como posso te ajudar?`
}]);

} catch (err) {
toast.dismiss(loadingChat);
console.error("Erro no chat auth:", err);
toast.error(err.response?.data?.error || "Erro ao iniciar chat.");
}
};

const handleReact = async (msgId, emoji) => {
if (!msgId || String(msgId).startsWith('temp_')) return;

setChatHistory(prev => prev.map(m =>
m.msg_id === msgId ? { ...m, reaction: emoji } : m
));
setHoveredMsgId(null);

try {
let webchatId = localStorage.getItem('webchat_id');
await api.post(`/whatsapp/react/${dados.estabelecimento.id}`, {
 msgId,
 emoji,
 remoteJid: webchatId
});
} catch (err) {
console.error("Erro ao enviar reação:", err);
}
};

const calcularTaxaPeloCEP = async (cep) => {
const cepLimpo = cep.replace(/\D/g, "");
if (cepLimpo.length !== 8) return toast.error("CEP incompleto");

const loadingToast = toast.loading("Calculando frete...");

try {
const resViaCep = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
const dataCep = await resViaCep.json();

if (dataCep.erro) {
 toast.dismiss(loadingToast);
 return toast.error("CEP não encontrado");
}

const enderecoObj = {
 rua: dataCep.logradouro,
 bairro: dataCep.bairro,
 cidade: dataCep.localidade,
 estado: dataCep.uf,
 cep: cepLimpo,
 numero: ""
};

const resFrete = await api.post(`/cardapio/estabelecimentos/${dados.estabelecimento.id}/calcular-frete`, {
 endereco_cliente: enderecoObj
});

toast.dismiss(loadingToast);

if (resFrete.data.success) {
 const valor = parseFloat(resFrete.data.taxa_entrega);
 const distancia = resFrete.data.distancia_km;

 setTaxaEntregaSoma(valor);
 setCepCalculado(true);

 toast.success(
 `Entrega confirmada! Distância: ${distancia}km - Valor: R$ ${valor.toFixed(2).replace('.', ',')}`,
 { duration: 4000 }
 );
} else if (resFrete.data.fora_da_area) {
 setTaxaEntregaSoma(0);
 setCepCalculado(false);
 toast.error(resFrete.data.mensagem || "Fora da área de entrega", { duration: 5000 });
}

} catch (err) {
toast.dismiss(loadingToast);
console.error("Erro frete:", err);
toast.error(err.response?.data?.error || "Erro ao calcular frete");
}
};

const fecharModalProduto = () => {
setSelectedProduct(null);
setQuantidade(1);
setSelecaoGrupos({});
setMostrarQRCode(false);
setObservacao('');
};

const removeFromCart = (tempId) => {
setCart(cart.filter(item => item.tempId !== tempId));
toast.error("Removido da sacola");
};

const scrollSlider = (direction) => {
if (sliderRef.current) {
sliderRef.current.scrollBy({
 left: direction === 'left' ? -300 : 300,
 behavior: 'smooth'
});
}
};

const scrollCategorySlider = (catId, direction) => {
const el = categorySlidersRef.current[catId];
if (el) {
el.scrollBy({
 left: direction === 'left' ? -320 : 320,
 behavior: 'smooth'
});
}
};

const handleToggleItem = (grupo, item) => {
const selecionados = selecaoGrupos[grupo.id] || [];
const jaSelecionado = selecionados.find(i => i.nome === item.nome);
const max = parseInt(grupo.maximo) || 1;

if (jaSelecionado) {
setSelecaoGrupos({ ...selecaoGrupos, [grupo.id]: selecionados.filter(i => i.nome !== item.nome) });
} else {
if (selecionados.length < max) {
 setSelecaoGrupos({ ...selecaoGrupos, [grupo.id]: [...selecionados, item] });
} else if (max === 1) {
 setSelecaoGrupos({ ...selecaoGrupos, [grupo.id]: [item] });
} else {
 toast.error(`Máximo de ${max} itens permitidos.`);
}
}
};

const validarSelecao = () => {
if (!selectedProduct?.adicionais) return true;
return selectedProduct.adicionais.every(grupo => (selecaoGrupos[grupo.id] || []).length >= (parseInt(grupo.minimo) || 0));
};

return (
<div className="public-view-container" style={{ backgroundColor: '#f8fafc', minHeight: '100vh', paddingBottom: '90px' }}>
<style>{`
 .cat-pill-btn { transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
 .cat-pill-btn:hover { background-color: ${corPrimaria} !important; color: #ffffff !important; transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.15); }
 .product-list-main { margin-top: 30px !important; }
 .category-section-title { margin-bottom: 20px !important; border-bottom: 2px solid #f1f5f9; padding-bottom: 10px; }
 .list-item-card { transition: all 0.25s ease; }
 .list-item-card:hover { border-color: ${corPrimaria} !important; transform: translateX(4px); box-shadow: 0 6px 16px rgba(0,0,0,0.06) !important; }
`}</style>

{/* CONTAINER DO TOASTER COM Z-INDEX SUPERIOR AO MODAL */}
<Toaster position="bottom-center" containerStyle={{ zIndex: 999999 }} />

{/* MODAL DE ALERTA (POPUP INICIAL) */}
{showAlertModal && alertasList.length > 0 && (() => {
 const alertaAtual = alertasList[currentAlertIndex];
 const linkAcao = alertaAtual.link_acao || alertaAtual.link || alertaAtual.url;
 const textoBotao = alertaAtual.botao_texto || alertaAtual.texto_botao || 'Saiba mais';

 return (
 <div className="modal-view-overlay" style={{ zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
 <div className="modal-view-sheet animate-fade-in" style={{ height: 'auto', maxHeight: '90vh', width: '100%', maxWidth: '400px', borderRadius: '24px', position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
 
 <button onClick={handleCloseAlert} style={{ position: 'absolute', top: '15px', right: '15px', background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none', borderRadius: '50%', width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 10 }}>
  <X size={18} />
 </button>

 {alertaAtual.imagem_url && (
  <img
  src={alertaAtual.imagem_url.startsWith('http') ? alertaAtual.imagem_url : `https://app.universomidiadigital.com.br${alertaAtual.imagem_url}`}
  alt={alertaAtual.titulo}
  style={{ width: '100%', height: '200px', objectFit: 'cover' }}
  />
 )}

 <div style={{ padding: '25px 20px', textAlign: 'center', background: '#fff' }}>
  <h2 style={{ margin: '0 0 10px 0', fontSize: '20px', color: '#333', fontWeight: '800' }}>{alertaAtual.titulo}</h2>
 
  {alertaAtual.mensagem && (
  <p style={{ color: '#64748b', fontSize: '14px', lineHeight: '1.5', marginBottom: '25px', whiteSpace: 'pre-wrap' }}>
  {alertaAtual.mensagem}
  </p>
  )}

  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
  {linkAcao && (
  <button
  onClick={async () => {
  try {
   const webchatId = localStorage.getItem('webchat_id') || null;
   const clienteData = JSON.parse(localStorage.getItem('clienteLogado') || 'null');
  
   await api.post(`/marketing/alerta/${alertaAtual.id}/clique`, {
   acao: 'clique',
   session_id: webchatId,
   cliente_id: clienteData?.id || null
   });
  } catch (err) {
   console.error("Erro ao registrar estatística de clique:", err);
  }
  
  const finalLink = linkAcao.startsWith('http') ? linkAcao : `https://${linkAcao}`;
  window.open(finalLink, '_blank');
  handleCloseAlert();
  }}
  style={{ background: corPrimaria, color: '#fff', border: 'none', padding: '12px', borderRadius: '12px', fontWeight: 'bold', cursor: 'pointer', fontSize: '15px', boxShadow: '0 4px 10px rgba(0,0,0,0.1)' }}
  >
  {textoBotao}
  </button>
  )}

  <button
  onClick={handleCloseAlert}
  style={{ background: linkAcao ? '#f1f5f9' : corPrimaria, color: linkAcao ? '#334155' : '#fff', border: 'none', padding: '12px', borderRadius: '12px', fontWeight: 'bold', cursor: 'pointer', fontSize: '15px', boxShadow: '0 4px 10px rgba(0,0,0,0.1)' }}
  >
  {linkAcao ? 'Fechar' : (currentAlertIndex < alertasList.length - 1 ? 'Próximo' : 'Entendi')}
  </button>

  <button
  onClick={() => handleDontShowAgain(alertaAtual.id)}
  style={{ background: 'transparent', color: '#94a3b8', border: 'none', padding: '10px', fontSize: '13px', cursor: 'pointer', textDecoration: 'underline' }}
  >
  Não mostrar novamente
  </button>
  </div>
 </div>
 </div>
 </div>
 );
})()}

{/* WEBCHAT DINÂMICO */}
{estabelecimento.webchat_ativo === 1 && (
 <div className="webchat-wrapper" style={{
    position: 'fixed',
    bottom: viewMode === 'cliente' ? '100px' : (cart.length > 0 && !showCartModal && viewMode === 'menu' ? '90px' : '20px'),
    [webchatSettings?.position === 'bottom-left' ? 'left' : 'right']: '20px',
    zIndex: 99999,
    transition: 'bottom 0.3s ease'
 }}>
 {showChat && (
 <div className="webchat-window animate-slide-up">
 <div className="webchat-header" style={{ backgroundColor: webchatSettings?.primaryColor || corPrimaria }}>
  <div className="webchat-header-info">
  <Globe size={18} />
  <span>{webchatSettings?.title || (isChatAuth ? 'Atendimento Online' : 'Identifique-se')}</span>
  </div>

  <div className="webchat-header-actions" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
  {isChatAuth && (
  <button onClick={handleLogout} title="Sair" className="webchat-logout-btn">
  <LogOut size={16} />
  </button>
  )}
  <button onClick={() => setShowChat(false)} className="webchat-close-btn">
  <X size={20} />
  </button>
  </div>
 </div>

 {!isChatAuth ? (
  <form className="webchat-form-auth" onSubmit={handleStartChat}>
  {webchatSettings?.showWelcomeMsg && (
  <p className="chat-welcome-text">{webchatSettings.welcomeMsg}</p>
  )}

  {webchatSettings?.fields?.filter(f => f.active).map(field => (
  <div className="chat-input-group" key={field.id}>
  <label>
   {field.label} {field.required && <span style={{color: 'red'}}>*</span>}
  </label>
  <input
   required={field.required}
   type={field.id === 'phone' ? 'tel' : field.id === 'email' ? 'email' : 'text'}
   value={chatForm[field.id] || ''}
   onChange={e => setChatForm({ ...chatForm, [field.id]: e.target.value })}
   placeholder={`Digite seu ${field.label.toLowerCase()}`}
  />
  </div>
  ))}

  <button
  type="submit"
  className="btn-start-chat"
  style={{ backgroundColor: webchatSettings?.primaryColor || corPrimaria }}
  >
  {webchatSettings?.buttonText || 'Iniciar Atendimento'}
  </button>
  </form>
 ) : (
  <>
  <div className="webchat-body custom-scrollbar">
  {chatHistory.map((m, i) => (
  <div
   key={i}
   className={`webchat-msg ${m.from}`}
   onMouseEnter={() => setHoveredMsgId(m.msg_id)}
   onMouseLeave={() => setHoveredMsgId(null)}
   style={{
   display: 'flex',
   width: '100%',
   justifyContent: m.from === 'me' ? 'flex-end' : 'flex-start',
   alignItems: 'center',
   gap: '8px',
   marginBottom: '10px'
   }}
  >
  
   <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
   <div
   className="webchat-bubble"
   style={{
   ...(m.from === 'me' ? { backgroundColor: webchatSettings?.primaryColor || corPrimaria, color: '#fff' } : { backgroundColor: '#f1f5f9', color: '#333' }),
   position: 'relative',
   maxWidth: '220px',
   padding: '10px',
   borderRadius: '10px',
   wordBreak: 'break-word'
   }}
   onDoubleClick={() => m.from === 'bot' && handleReact(m.msg_id, '❤️')}
   title={m.from === 'bot' ? "Clique duas vezes para curtir!" : ""}
   >
   {renderMedia(m)}
   {m.text && <div>{m.text}</div>}

   {m.reaction && (
   <div className="reaction-badge" style={{
    position: 'absolute',
    bottom: '-10px',
    [m.from === 'me' ? 'left' : 'right']: '-5px',
    background: '#fff',
    border: '1px solid #eee',
    borderRadius: '20px',
    padding: '2px 5px',
    fontSize: '12px',
    boxShadow: '0 2px 5px rgba(0,0,0,0.15)',
    zIndex: 2,
    color: '#000'
   }}>
    {m.reaction}
   </div>
   )}
   </div>

   {m.from === 'bot' && m.msg_id && hoveredMsgId === m.msg_id && (
   <div
   className="reaction-menu-bar"
   style={{
    position: 'absolute',
    right: '-165px',
    display: 'flex',
    gap: '4px',
    background: '#fff',
    padding: '4px 8px',
    borderRadius: '20px',
    border: '1px solid #e9ecef',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    zIndex: 10
   }}
   >
   {['👍', '❤️', '😂', '😮', '😢', '🙏'].map(emoji => (
    <button
    key={emoji}
    onClick={() => handleReact(m.msg_id, emoji)}
    style={{
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '16px',
    padding: '2px',
    transition: 'transform 0.1s ease',
    outline: 'none'
    }}
    onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.3)'}
    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
    title={`Reagir com ${emoji}`}
    >
    {emoji}
    </button>
   ))}
   </div>
   )}
   </div>
  </div>
  ))}
  <div ref={chatEndRef} />
  </div>
  
  <form className="webchat-footer" onSubmit={handleSendWebchat} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px', background: '#fff', borderTop: '1px solid #eee' }}>

  {allowUpload && (
  <>
   <button type="button" onClick={() => fileInputRef.current?.click()} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#666', padding: '5px' }}>
   <Paperclip size={20} />
   </button>
   <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileSelect} accept="image/*,video/*,audio/*,.pdf,.doc,.docx" />
  </>
  )}
  
  {isRecordingWebchat ? (
  <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fee2e2', borderRadius: '20px', padding: '5px 15px' }}>
   <span style={{ color: '#ef4444', fontWeight: 'bold', fontSize: '13px', animation: 'pulse 1.5s infinite' }}>
   Gravando... {formatTime(recordingTimeWebchat)}
   </span>
   <button type="button" onClick={cancelRecordingWebchat} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}>
   <Trash2 size={16} />
   </button>
  </div>
  ) : fileToUpload ? (
  <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f1f5f9', borderRadius: '20px', padding: '5px 15px', fontSize: '13px' }}>
   <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '150px', color: '#333' }}>
   {fileToUpload.name}
   </span>
   <button type="button" onClick={() => { setFileToUpload(null); setFilePreviewUrl(null); }} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer' }}>
   <X size={16} />
   </button>
  </div>
  ) : (
  <input
   placeholder="Mensagem..."
   value={chatMsg}
   onChange={(e) => setChatMsg(e.target.value)}
   style={{ flex: 1, border: 'none', background: '#f1f5f9', padding: '10px 15px', borderRadius: '20px', outline: 'none' }}
  />
  )}
  
  {isRecordingWebchat ? (
  <button type="button" onClick={stopRecordingWebchat} style={{ color: webchatSettings?.primaryColor || corPrimaria, background: 'none', border: 'none', cursor: 'pointer', padding: '5px' }}>
   <Send size={20} />
  </button>
  ) : (!chatMsg.trim() && !fileToUpload) ? (
  <button type="button" onMouseDown={startRecordingWebchat} onMouseUp={stopRecordingWebchat} onTouchStart={startRecordingWebchat} onTouchEnd={stopRecordingWebchat} style={{ color: '#666', background: 'none', border: 'none', cursor: 'pointer', padding: '5px' }}>
   <Mic size={20} />
  </button>
  ) : (
  <button type="submit" style={{ color: webchatSettings?.primaryColor || corPrimaria, background: 'none', border: 'none', cursor: 'pointer', padding: '5px' }}>
   <Send size={20} />
  </button>
  )}
  </form>
  </>
 )}
 </div>
 )}

 <button
 className="webchat-trigger"
 style={{ backgroundColor: webchatSettings?.primaryColor || corPrimaria }}
 onClick={() => setShowChat(!showChat)}
 >
 {showChat ? <X size={28} /> : <MessageSquare size={28} />}
 </button>
 </div>
)}

{/* MENU LATERAL (DRAWER) */}
<div className={`menu-drawer-overlay ${isMenuOpen ? 'active' : ''}`} onClick={() => setIsMenuOpen(false)}>
 <aside className="menu-drawer-content" onClick={e => e.stopPropagation()}>
 <div
 className="menu-drawer-user"
 style={{ backgroundColor: corPrimaria, cursor: 'pointer' }}
 onClick={() => {
 setViewMode('cliente');
 setIsMenuOpen(false);
 }}
 >
 <div className="user-info" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
 <div style={{ background: 'rgba(255,255,255,0.2)', padding: '8px', borderRadius: '50%' }}>
  <User size={28} color="#fff" />
 </div>
 <div>
  <strong style={{ color: '#fff' }}>Minha Conta</strong>
  <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)', margin: 0 }}>Ver meus pedidos</p>
 </div>
 </div>
 <button onClick={(e) => { e.stopPropagation(); setIsMenuOpen(false); }} className="close-drawer">
 <X size={24} />
 </button>
 </div>

 <div className="menu-drawer-links">
 <button
 onClick={() => {
  setViewMode('cliente');
  setIsMenuOpen(false);
 }}
 className="nav-link"
 style={{ borderBottom: '1px solid #f1f5f9', marginBottom: '10px', paddingBottom: '15px' }}
 >
 <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
  <Package size={18} /> <strong>Meus Pedidos</strong>
 </div>
 <ChevronRight size={16} />
 </button>

 <span className="nav-label">Categorias do Cardápio</span>

 {categorias.map(cat => (
 <button
  key={cat.id}
  onClick={() => {
  setViewMode('menu');
  setTimeout(() => {
  document.getElementById(`cat-${cat.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, 100);
  setIsMenuOpen(false);
  }}
  className="nav-link"
 >
  {cat.nome} <ChevronRight size={16} />
 </button>
 ))}

 {viewMode === 'cliente' && (
 <button
  onClick={() => {
  setViewMode('menu');
  setIsMenuOpen(false);
  }}
  className="nav-link"
  style={{ marginTop: '20px', color: corPrimaria, fontWeight: 'bold' }}
 >
  <ChevronLeft size={18} /> Voltar ao Cardápio
 </button>
 )}
 </div>
 </aside>
</div>

{/* HEADER */}
<header className="store-header" style={headerStyle}>
 <button className="btn-menu-header"
 onClick={() => setIsMenuOpen(true)}
 style={{ backgroundColor: corPrimaria }}>
 <Menu size={24} color="#fff" />
 </button>
 <div className="store-info-box">
 <img src={logo} alt="Logo" className="store-logo-img" />
 <h1 className="store-title-main">
 {tipo === 'pizzaria' && '🍕 '}
 {tipo === 'lanchonete' && '🍔 '}
 {tipo === 'restaurante' && '🍽️ '}
 {estabelecimento.nome}
 </h1>
 <p className="store-info-line"><MapPin size={14} /> {estabelecimento.endereco || 'Endereço não informado'}</p>

 <div className="header-status-strip">
 <div className="status-item" style={{ backgroundColor: aberto ? '#2e7d32' : '#c62828' }}>
 <Clock size={12} /> {aberto ? 'Aberto agora' : 'Fechado'}
 </div>
 </div>

 <div className="social-container">
 {estabelecimento.instagram && (
 <a href={`https://instagram.com/${estabelecimento.instagram.replace('@', '')}`} target="_blank" rel="noreferrer" className="social-pill">
  <Instagram size={16} /> <span>Instagram</span>
 </a>
 )}
 {estabelecimento.whatsapp && (
 <a href={`https://wa.me/55${estabelecimento.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="social-pill">
  <img src="/assets/whats.png" alt="Zap" style={{ width: '16px' }} /> <span>WhatsApp</span>
 </a>
 )}
 </div>
 </div>
</header>

{/* ÁREA DINÂMICA: CLIENTE OU CARDÁPIO */}
{viewMode === 'cliente' ? (
 !clienteLogado ? (
 <section className="cliente-area-section-new">
 <div className="login-container-wrapper">
 <button
  onClick={() => setViewMode('menu')}
  className="btn-back-menu"
  style={{ color: corPrimaria, background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 'bold', marginBottom: '20px' }}
 >
  <ChevronLeft size={20} /> Voltar ao Cardápio
 </button>

 <div className="login-card-elevated" style={{ background: '#fff', padding: '30px', borderRadius: '24px', boxShadow: '0 10px 25px rgba(0,0,0,0.05)', textAlign: 'center' }}>
  <div className="login-icon-circle" style={{ backgroundColor: `${corPrimaria}15`, width: '64px', height: '64px', borderRadius: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
  <User size={32} color={corPrimaria} />
  </div>
 
  <div className="login-header-text">
  <h2 style={{ fontWeight: '900', fontSize: '24px', margin: '0 0 8px 0' }}>Acessar Minha Conta</h2>
  <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '25px' }}>Informe seus dados para acompanhar seus pedidos.</p>
  </div>

  <div className="login-form-fields" style={{ textAlign: 'left' }}>
  <div className="input-group-custom" style={{ marginBottom: '20px' }}>
  <label style={{ fontSize: '11px', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', display: 'block', marginBottom: '8px' }}>E-mail</label>
  <input
  type="email"
  placeholder="exemplo@email.com"
  id="email-login"
  style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1.5px solid #e2e8f0', outline: 'none' }}
  />
  </div>

  <div className="input-group-custom">
  <label style={{ fontSize: '11px', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', display: 'block', marginBottom: '8px' }}>Código de Acesso</label>
  <input
  type="text"
  placeholder="000000"
  maxLength="6"
  style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1.5px solid #e2e8f0', outline: 'none', textAlign: 'center', fontSize: '24px', fontWeight: 'bold', letterSpacing: '4px' }}
  onChange={(e) => {
   if (e.target.value.length === 6) {
   const emailVal = document.getElementById('email-login').value;
   handleLoginToken(e.target.value, emailVal);
   }
  }}
  />
  <small style={{ display: 'block', marginTop: '8px', color: '#94a3b8', fontSize: '11px' }}>Insira o código enviado para seu e-mail</small>
  </div>
  </div>
 </div>

 <footer className="login-footer" style={{ marginTop: '40px', textAlign: 'center', color: '#94a3b8' }}>
  <p style={{ fontSize: '13px', fontWeight: '700', margin: '0' }}>© 2026 Universo Mídia Digital</p>
  <small style={{ fontSize: '11px' }}>Tecnologia para Delivery e Cardápios Digitais</small>
 </footer>
 </div>
 </section>
 ) : (
 <ClienteArea
 clienteLogado={clienteLogado}
 pedidosCliente={pedidosCliente}
 corPrimaria={corPrimaria}
 setViewMode={setViewMode}
 setClienteLogado={setClienteLogado}
 setPedidosCliente={setPedidosCliente}
 />
 )
) : (
 <>
 {/* BANNERS PROMOCIONAIS */}
 {bannersList.length > 0 && (
 <section className="store-banners-slider" style={{ margin: '20px auto', maxWidth: '1200px', padding: '0 15px', position: 'relative' }}>
 <div style={{ overflow: 'hidden', borderRadius: '20px', boxShadow: '0 8px 20px rgba(0,0,0,0.08)' }}>
  <div style={{
  display: 'flex',
  transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
  transform: `translateX(-${currentBannerIndex * 100}%)`
  }}>
  {bannersList.map(banner => (
  <div key={banner.id} style={{ minWidth: '100%', flex: '0 0 100%', position: 'relative' }}>
  <a href={banner.link_url || '#'} target={banner.link_url ? '_blank' : '_self'} rel="noreferrer" style={{ display: 'block' }}>
   <img
   src={banner.imagem_url.startsWith('http') ? banner.imagem_url : `https://app.universomidiadigital.com.br${banner.imagem_url}`}
   alt={banner.titulo || 'Banner Promocional'}
   style={{ width: '100%', height: '300px', objectFit: 'cover', display: 'block' }}
   />
   {/* Overlay com Título e Descrição */}
   {(banner.titulo || banner.descricao) && (
   <div style={{
   position: 'absolute',
   bottom: 0, left: 0, right: 0,
   background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0) 100%)',
   padding: '40px 20px 20px 20px',
   color: '#fff',
   textAlign: 'left'
   }}>
   {banner.titulo && <h3 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: '900', textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>{banner.titulo}</h3>}
   {banner.descricao && <p style={{ margin: 0, fontSize: '15px', opacity: 0.9, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>{banner.descricao}</p>}
   </div>
   )}
  </a>
  </div>
  ))}
  </div>
 </div>
 
 {/* Indicadores do Slider (Dots) */}
 {bannersList.length > 1 && (
  <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '16px' }}>
  {bannersList.map((_, idx) => (
  <button
  key={idx}
  onClick={() => setCurrentBannerIndex(idx)}
  style={{
   width: currentBannerIndex === idx ? '28px' : '10px',
   height: '10px',
   borderRadius: '5px',
   border: 'none',
   backgroundColor: currentBannerIndex === idx ? corPrimaria : '#cbd5e1',
   cursor: 'pointer',
   transition: 'all 0.3s ease'
  }}
  aria-label={`Ir para o banner ${idx + 1}`}
  />
  ))}
  </div>
 )}
 </section>
 )}

 {/* BARRA DE PESQUISA IFOOD STYLE & ALTERNADOR DE LAYOUT (GRADE / LISTA) */}
 <div style={{ maxWidth: '1200px', margin: '15px auto', padding: '0 15px', display: 'flex', gap: '10px', alignItems: 'center' }}>
 <div style={{ position: 'relative', display: 'flex', alignItems: 'center', flex: 1 }}>
 <Search size={20} color="#94a3b8" style={{ position: 'absolute', left: '16px' }} />
 <input
  type="text"
  placeholder="Buscar no cardápio (ex: lanche, bebida...)"
  value={searchTerm}
  onChange={(e) => setSearchTerm(e.target.value)}
  style={{
  width: '100%',
  padding: '14px 16px 14px 48px',
  borderRadius: '16px',
  border: '1px solid #e2e8f0',
  backgroundColor: '#fff',
  fontSize: '15px',
  outline: 'none',
  boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
  }}
 />
 {searchTerm && (
  <button
  onClick={() => setSearchTerm('')}
  style={{ position: 'absolute', right: '16px', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}
  >
  <X size={18} />
  </button>
 )}
 </div>

 {/* Botões de Alternância de Layout (Grade / Lista iFood) */}
 <div style={{ display: 'flex', background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '4px', gap: '4px', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
 <button
  onClick={() => setLayoutStyle('grid')}
  title="Visualizar em Grade"
  style={{
  background: layoutStyle === 'grid' ? corPrimaria : 'transparent',
  color: layoutStyle === 'grid' ? '#fff' : '#64748b',
  border: 'none',
  borderRadius: '12px',
  padding: '10px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.2s'
  }}
 >
  <LayoutGrid size={20} />
 </button>
 <button
  onClick={() => setLayoutStyle('list')}
  title="Visualizar em Lista (Estilo iFood)"
  style={{
  background: layoutStyle === 'list' ? corPrimaria : 'transparent',
  color: layoutStyle === 'list' ? '#fff' : '#64748b',
  border: 'none',
  borderRadius: '12px',
  padding: '10px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.2s'
  }}
 >
  <List size={20} />
 </button>
 </div>
 </div>

 {itensDestaque.length > 0 && !searchTerm && (
 <section className="destaques-slider-section" style={{ maxWidth: '1200px', margin: '0 auto 20px', padding: '0 15px' }}>
 <div className="destaques-header-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
  <h3 className="section-title-padding" style={{ fontSize: '18px', fontWeight: '800', color: '#1e293b', margin: 0 }}>Destaques da Casa</h3>
  <div className="slider-arrows" style={{ display: 'flex', gap: '6px' }}>
  <button className="arrow-btn" onClick={() => scrollSlider('left')} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
  <ChevronLeft size={18} />
  </button>
  <button className="arrow-btn" onClick={() => scrollSlider('right')} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
  <ChevronRight size={18} />
  </button>
  </div>
 </div>

 <div className="destaques-container-wrapper">
  <div className="destaques-row custom-scrollbar" ref={sliderRef} style={{ display: 'flex', gap: '15px', overflowX: 'auto', scrollSnapType: 'x mandatory', paddingBottom: '10px', scrollbarWidth: 'none' }}>
  {itensDestaque.map(prod => (
  <div key={prod.id} className="destaque-card" onClick={async () => {
    setSelectedProduct(prod);
    try {
      await api.post('/cardapio/produtos/clique', {
        estabelecimento_id: dados.estabelecimento.id,
        produto_id: prod.id
      });
    } catch (e) {
      console.error("Erro ao registrar clique:", e);
    }
  }} style={{ minWidth: '160px', width: '160px', background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', cursor: 'pointer', flexShrink: 0, boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
  <div className="destaque-card-img" style={{ position: 'relative', height: '120px' }}>
   <img src={prod.imagem_url || '/placeholder.png'} alt={prod.nome} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
   <div className="destaque-price-tag" style={{ backgroundColor: corPrimaria, position: 'absolute', bottom: '8px', right: '8px', color: '#fff', padding: '2px 8px', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold' }}>
   R$ {parseFloat(prod.preco).toFixed(2).replace('.', ',')}
   </div>
  </div>
  <div className="destaque-card-info" style={{ padding: '10px' }}>
   <p style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{prod.nome}</p>
  </div>
  </div>
  ))}
  </div>
 </div>
 </section>
 )}

 <nav className="cat-sticky-nav" style={{ display: 'flex', gap: '10px', overflowX: 'auto', padding: '10px 15px', maxWidth: '1200px', margin: '0 auto', background: '#fff', borderRadius: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.02)', position: 'sticky', top: '10px', zIndex: 100, scrollbarWidth: 'none' }}>
 {categorias.map(cat => (
 <button key={cat.id} onClick={() => document.getElementById(`cat-${cat.id}`)?.scrollIntoView({ behavior: 'smooth' })} className="cat-pill-btn" style={{ whiteSpace: 'nowrap', background: '#f1f5f9', border: 'none', padding: '8px 16px', borderRadius: '20px', fontWeight: '600', color: '#475569', cursor: 'pointer' }}>{cat.nome}</button>
 ))}
 </nav>

 <main className="product-list-main" style={{ maxWidth: '1200px', margin: '20px auto 0', padding: '0 15px' }}>
 {filteredCategorias.length === 0 ? (
 <div style={{ textAlign: 'center', padding: '40px', background: '#fff', borderRadius: '16px', color: '#64748b' }}>
  <p>Nenhum produto encontrado para "{searchTerm}"</p>
 </div>
 ) : (
 filteredCategorias.map(cat => {
  const itensDaCategoria = itensCardapio
  ?.filter(i => Number(i.categoria_id) === Number(cat.id))
  .filter(i => {
  if (!searchTerm.trim()) return true;
  const term = searchTerm.toLowerCase();
  return i.nome.toLowerCase().includes(term) || (i.descricao && i.descricao.toLowerCase().includes(term));
  })
  .sort((a, b) => Number(a.ordem || 0) - Number(b.ordem || 0)) || [];

  if (itensDaCategoria.length === 0) return null;

  return (
  <section key={cat.id} id={`cat-${cat.id}`} style={{ marginBottom: '35px' }}>
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
  <h3 className="category-section-title" style={{ fontSize: '20px', fontWeight: '800', color: '#1e293b', margin: 0 }}>{cat.nome}</h3>
  
  {layoutStyle === 'grid' && (
   <div style={{ display: 'flex', gap: '6px' }}>
   <button
   onClick={() => scrollCategorySlider(cat.id, 'left')}
   style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#334155' }}
   >
   <ChevronLeft size={18} />
   </button>
   <button
   onClick={() => scrollCategorySlider(cat.id, 'right')}
   style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#334155' }}
   >
   <ChevronRight size={18} />
   </button>
   </div>
  )}
  </div>
  
  {layoutStyle === 'grid' ? (
  <div
   ref={el => categorySlidersRef.current[cat.id] = el}
   className="custom-scrollbar"
   style={{
   display: 'flex',
   gap: '16px',
   overflowX: 'auto',
   scrollSnapType: 'x mandatory',
   paddingBottom: '10px',
   scrollbarWidth: 'none',
   scrollBehavior: 'smooth'
   }}
  >
   {itensDaCategoria.map(prod => (
   <div
   key={prod.id}
   onClick={async () => {
    setSelectedProduct(prod);
    try {
      await api.post('/cardapio/produtos/clique', {
        estabelecimento_id: dados.estabelecimento.id,
        produto_id: prod.id
      });
    } catch (e) {
      console.error("Erro ao registrar clique:", e);
    }
   }}
   style={{
   minWidth: '280px',
   maxWidth: '280px',
   flex: '0 0 280px',
   display: 'flex',
   flexDirection: 'column',
   justifyContent: 'space-between',
   background: '#fff',
   borderRadius: '16px',
   border: '1px solid #e2e8f0',
   cursor: 'pointer',
   boxShadow: '0 4px 15px rgba(0,0,0,0.03)',
   overflow: 'hidden',
   position: 'relative',
   transition: 'transform 0.2s ease, box-shadow 0.2s ease',
   scrollSnapAlign: 'start'
   }}
   onMouseEnter={(e) => {
   e.currentTarget.style.transform = 'translateY(-3px)';
   e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.08)';
   }}
   onMouseLeave={(e) => {
   e.currentTarget.style.transform = 'translateY(0)';
   e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.03)';
   }}
   >
   <div style={{ position: 'relative', width: '100%', height: '140px', backgroundColor: '#f8fafc' }}>
   <img
    src={prod.imagem_url || '/placeholder.png'}
    alt={prod.nome}
    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
   />
   <div style={{
    position: 'absolute',
    bottom: '8px',
    right: '8px',
    background: '#fff',
    borderRadius: '50%',
    padding: '6px',
    color: corPrimaria,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
   }}>
    <Plus size={16} strokeWidth={3} />
   </div>
   </div>

   <div style={{ padding: '14px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', flex: 1 }}>
   <div>
    <h4 style={{ margin: '0 0 6px 0', fontSize: '15px', color: '#1e293b', fontWeight: '700', lineHeight: '1.2' }}>
    {prod.nome}
    </h4>
    <p style={{
    margin: '0 0 12px 0',
    fontSize: '13px',
    color: '#64748b',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    lineHeight: '1.4'
    }}>
    {prod.descricao}
    </p>
   </div>
   <strong style={{ color: corPrimaria, fontSize: '16px', fontWeight: '800' }}>
    R$ {parseFloat(prod.preco || 0).toFixed(2).replace('.', ',')}
   </strong>
   </div>
   </div>
   ))}
  </div>
  ) : (
  /* Estilo de Lista iFood */
  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
   {itensDaCategoria.map(prod => (
   <div
   key={prod.id}
   className="list-item-card"
   onClick={async () => {
     setSelectedProduct(prod);
     try {
       await api.post('/cardapio/produtos/clique', {
         estabelecimento_id: dados.estabelecimento.id,
         produto_id: prod.id
       });
     } catch (e) {
       console.error("Erro ao registrar clique:", e);
     }
   }}
   style={{
   display: 'flex',
   justifyContent: 'space-between',
   alignItems: 'center',
   background: '#fff',
   borderRadius: '16px',
   border: '1px solid #e2e8f0',
   padding: '16px',
   cursor: 'pointer',
   boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
   transition: 'all 0.2s'
   }}
   onMouseEnter={(e) => {
   e.currentTarget.style.borderColor = corPrimaria;
   e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.06)';
   }}
   onMouseLeave={(e) => {
   e.currentTarget.style.borderColor = '#e2e8f0';
   e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.02)';
   }}
   >
   <div style={{ flex: 1, paddingRight: '15px' }}>
   <h4 style={{ margin: '0 0 6px 0', fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>{prod.nome}</h4>
   <p style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#64748b', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
    {prod.descricao}
   </p>
   <strong style={{ color: corPrimaria, fontSize: '15px', fontWeight: '800' }}>
    R$ {parseFloat(prod.preco || 0).toFixed(2).replace('.', ',')}
   </strong>
   </div>
   {prod.imagem_url && (
   <div style={{ position: 'relative', width: '90px', height: '90px', borderRadius: '12px', overflow: 'hidden', flexShrink: 0 }}>
    <img src={prod.imagem_url} alt={prod.nome} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
   </div>
   )}
   </div>
   ))}
  </div>
  )}
  </section>
  );
 })
 )}
 </main>
 </>
)}

{selectedProduct && (
 <div className="modal-view-overlay" onClick={fecharModalProduto}>
 <div className="modal-view-sheet" onClick={e => e.stopPropagation()}>
 <div className="modal-view-scroll">
 <button className="btn-close-modal" onClick={fecharModalProduto}><X /></button>
 {selectedProduct.imagem_url && <img src={selectedProduct.imagem_url} className="modal-view-banner" alt="" />}
 <div className="modal-view-content">
  <h2>{selectedProduct.nome}</h2>
  <p>{selectedProduct.descricao}</p>
 
  {Number(selectedProduct.exibir_qrcode) === 1 && (
  <div className="product-qrcode-section" style={{marginTop: '15px', marginBottom: '15px'}}>
  <button
  className="btn-toggle-qrcode"
  onClick={() => setMostrarQRCode(!mostrarQRCode)}
  style={{
   backgroundColor: `${corPrimaria}15`,
   color: corPrimaria,
   border: 'none',
   padding: '10px 15px',
   borderRadius: '8px',
   fontWeight: 'bold',
   cursor: 'pointer',
   display: 'flex',
   alignItems: 'center',
   gap: '8px',
   fontSize: '14px',
   width: 'fit-content'
  }}
  >
  <QrCode size={18} /> {mostrarQRCode ? 'Ocultar QR Code' : 'Mostrar QR Code'}
  </button>

  {mostrarQRCode && (
  <div className="qrcode-display-box animate-fade-in" style={{ marginTop: '15px', textAlign: 'center', padding: '15px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
   <img
   src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(window.location.origin + window.location.pathname + '?produto=' + selectedProduct.id)}`}
   alt="QR Code do Produto"
   style={{ borderRadius: '8px', width: '150px', height: '150px' }}
   />
   <p style={{ fontSize: '12px', color: '#64748b', marginTop: '10px', marginBottom: 0 }}>Escaneie para acessar o produto</p>
  </div>
  )}
  </div>
  )}

  {[...(selectedProduct.adicionais || [])]
  .sort((a, b) => Number(a.ordem || 0) - Number(b.ordem || 0))
  .map(grupo => {
  let itensDoGrupo = [];
  try {
  itensDoGrupo = typeof grupo.itens_json === 'string' ? JSON.parse(grupo.itens_json) : (grupo.itens_json || []);
  } catch (e) { itensDoGrupo = []; }

  return (
  <details key={grupo.id} className="modal-group-accordion" open={grupo.minimo > 0}>
   <summary className="modal-group-summary">
   <div>
   <strong>{grupo.nome}</strong>
   <small>{grupo.minimo > 0 ? 'Obrigatório' : 'Opcional'} • {selecaoGrupos[grupo.id]?.length || 0}/{grupo.maximo}</small>
   </div>
   <ChevronDown size={18} />
   </summary>
   <div className="modal-group-options">
   {itensDoGrupo.map((it, idx) => (
   <label key={idx} className="modal-option-row">
   <input
    type={parseInt(grupo.maximo) === 1 ? "radio" : "checkbox"}
    checked={!!(selecaoGrupos[grupo.id] || []).find(s => s.nome === it.nome)}
    onChange={() => handleToggleItem(grupo, it)}
   />
   <span>{it.nome}</span>
   {parseFloat(it.preco) > 0 && <span className="opt-price">+ R$ {parseFloat(it.preco).toFixed(2).replace('.', ',')}</span>}
   </label>
   ))}
   </div>
  </details>
  );
  })}
 </div>
 </div>

 {/* Campo de Observação */}
 <div style={{ padding: '15px 20px', borderTop: '1px solid #e2e8f0', backgroundColor: '#fff' }}>
 <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold', color: '#334155' }}>
  Alguma observação?
 </label>
 <textarea
  placeholder="Ex: Sem cebola, maionese à parte..."
  value={observacao}
  onChange={(e) => setObservacao(e.target.value)}
  style={{
  width: '100%',
  padding: '12px',
  borderRadius: '12px',
  border: '1px solid #cbd5e1',
  resize: 'none',
  height: '70px',
  fontFamily: 'inherit',
  fontSize: '14px',
  outlineColor: corPrimaria
  }}
 />
 </div>

 <div className="modal-view-footer">
 <div className="qty-selector">
  <button onClick={() => setQuantidade(Math.max(1, quantidade - 1))}>
  <Minus size={18} />
  </button>

  <span>{quantidade}</span>

  <button onClick={() => setQuantidade(quantidade + 1)}>
  <Plus size={18} />
  </button>
 </div>

 <button
  className="btn-confirm-add"
  style={{ backgroundColor: corPrimaria }}
  disabled={!validarSelecao()}
  onClick={() => {
  setCart([
  ...cart,
  {
  ...selectedProduct,
  tempId: Date.now(),
  quantidade,
  precoTotal: precoFinalExibicao,
  escolhas: selecaoGrupos,
  observacao
  }
  ]);
  fecharModalProduto();
  toast.success("Adicionado!");
  }}
 >
  Adicionar • R$ {precoFinalExibicao.toFixed(2).replace('.', ',')}
 </button>
 </div>
 </div>
 </div>
)}

{showCartModal && (
 <div className="modal-view-overlay" onClick={() => setShowCartModal(false)}>
 <div className="modal-view-sheet cart-modal" onClick={e => e.stopPropagation()}>
 {step === 'cart' ? (
 <>
  <div className="modal-view-content">
  <div className="cart-header-title">
  <h2 style={{ margin: 0 }}>Minha Sacola</h2>
  <button onClick={() => setShowCartModal(false)} className="btn-close-cart"><X size={24} /></button>
  </div>
  <div className="cart-items-list">
  {cart.length === 0 ? (
  <div className="empty-cart-state"><p>Sua sacola está vazia</p></div>
  ) : cart.map((item) => (
  <div key={item.tempId} className="cart-item-detailed">
   <div className="cart-item-main">
   <div className="cart-item-info">
   <span className="cart-item-qty" style={{ color: corPrimaria }}>{item.quantidade}x</span>
   <strong className="cart-item-name">{item.nome}</strong>
   </div>
   <div className="cart-item-price">R$ {item.precoTotal.toFixed(2).replace('.', ',')}</div>
   </div>
   <div className="cart-item-details-row">
   <div className="cart-item-extras">
   {Object.values(item.escolhas || {}).flat().map((esc, i) => (
   <p key={i} className="extra-text">+ {esc.nome}</p>
   ))}
   {item.observacao && (
   <p className="extra-text" style={{ fontStyle: 'italic', color: '#d97706', marginTop: '4px' }}>
    <strong style={{fontWeight: 600}}>Obs:</strong> {item.observacao}
   </p>
   )}
   </div>
   {item.imagem_url && <img src={item.imagem_url} alt="" className="cart-item-img-small" />}
   </div>
   <div className="cart-item-actions">
   <button onClick={() => removeFromCart(item.tempId)} className="btn-remove-cart" style={{ color: corPrimaria }}>Remover</button>
   </div>
  </div>
  ))}
  </div>
  </div>
  <div className="modal-view-footer">
  <div className="total-summary">
  <div className="summary-line">
  <span>Subtotal</span>
  <span>R$ {totalCart.toFixed(2).replace('.', ',')}</span>
  </div>
  
  {estabelecimento.faz_delivery === 1 && cart.length > 0 && (
  <div className="delivery-calculator-box" style={{ borderTop: '1px dashed #eee', marginTop: '10px', paddingTop: '10px' }}>
   <div className="summary-line" style={{ color: '#666', fontSize: '14px' }}>
   <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
   <Bike size={16} /> Entrega
   {taxasEntrega.metodo === 'raio' && cepCalculado && <small>(Por distância)</small>}
   </span>
   <span style={{ fontWeight: 'bold', color: cepCalculado ? corPrimaria : '#666' }}>
   {cepCalculado ? (taxaEntregaSoma > 0 ? `R$ ${taxaEntregaSoma.toFixed(2).replace('.', ',')}` : 'Grátis') : 'A calcular'}
   </span>
   </div>
  
   {!cepCalculado ? (
   <div className="cep-input-row" style={{ display: 'flex', gap: '5px', marginTop: '10px' }}>
   <input
   type="text"
   placeholder="Seu CEP para frete"
   className="input-cep-modern"
   maxLength="8"
   value={cepCliente}
   onChange={(e) => setCepCliente(e.target.value.replace(/\D/g, ""))}
   style={{ flex: 1, padding: '12px', borderRadius: '12px', border: '1.5px solid #eee', fontSize: '14px', outline: 'none' }}
   />
   <button
   onClick={() => calcularTaxaPeloCEP(cepCliente)}
   className="btn-calc-frete"
   style={{ backgroundColor: corPrimaria, color: '#fff', border: 'none', padding: '0 15px', borderRadius: '12px', cursor: 'pointer', fontWeight: 'bold' }}
   >
   Calcular
   </button>
   </div>
   ) : (
   <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '5px' }}>
   <small style={{ color: '#2e7d32', fontWeight: 'bold' }}>✓ Endereço localizado</small>
   <button
   onClick={() => { setTaxaEntregaSoma(0); setCepCliente(''); setCepCalculado(false); }}
   style={{ background: 'none', border: 'none', color: corPrimaria, fontSize: '12px', cursor: 'pointer', textDecoration: 'underline' }}
   >
   Alterar CEP
   </button>
   </div>
   )}
  </div>
  )}
  <div className="summary-line total-final" style={{ marginTop: '10px', borderTop: '1px solid #eee', paddingTop: '10px' }}>
  <strong>Total</strong>
  <strong style={{ fontSize: '18px', color: '#333' }}>
   R$ {(totalCart + (taxaEntregaSoma || 0)).toFixed(2).replace('.', ',')}
  </strong>
  </div>
  </div>
  <button disabled={cart.length === 0} className="btn-checkout-final" style={{ backgroundColor: corPrimaria }} onClick={() => setStep('checkout')}>Finalizar Pedido</button>
  </div>
 </>
 ) : (
 <EnviosApp
 cart={cart}
 total={totalCart}
 estabelecimento={estabelecimento}
 taxaEntregaSoma={Number(taxaEntregaSoma) || 0}
 cepInicial={cepCliente}
 onBack={() => setStep('cart')}
 onFinalize={() => {
 setCart([]);
 setShowCartModal(false);
 setStep('cart');
 setTaxaEntregaSoma(0);
 setCepCalculado(false);
 setCepCliente('');
 }}
 />
 )}
 </div>
 </div>
)}

{/* BARRA FLUTUANTE MODERNA */}
{cart.length > 0 && !showCartModal && viewMode === 'menu' && (
 <div
 className="floating-cart-bar-container"
 onClick={() => { setStep('cart'); setShowCartModal(true); }}
 style={{ position: 'fixed', bottom: '20px', left: '0', right: '0', padding: '0 20px', zIndex: '999' }}
 >
 <div className="floating-cart-bar" style={{ backgroundColor: corPrimaria, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px', borderRadius: '16px', color: '#fff', boxShadow: '0 10px 25px rgba(0,0,0,0.2)', cursor: 'pointer' }}>
 <div className="cart-left-content" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
 <div className="cart-icon-badge" style={{ background: '#fff', padding: '8px', borderRadius: '12px', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
  <Package size={20} color={corPrimaria} />
  <span className="cart-badge-count" style={{ position: 'absolute', top: '-5px', right: '-5px', background: '#333', color: '#fff', fontSize: '10px', fontWeight: 'bold', minWidth: '18px', height: '18px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{cart.length}</span>
 </div>
 <div className="cart-text-info" style={{ display: 'flex', flexDirection: 'column' }}>
  <strong style={{ fontSize: '16px', lineHeight: '1' }}>Ver sacola</strong>
  <ChevronDown size={16} style={{ transform: 'rotate(180deg)', opacity: '0.8' }} />
 </div>
 </div>
 
 <div className="cart-price-total" style={{ textAlign: 'right', display: 'flex', flexDirection: 'column' }}>
 <span style={{ fontSize: '11px', opacity: '0.9' }}>Total:</span>
 <strong style={{ fontSize: '16px' }}>R$ {totalCart.toFixed(2).replace('.', ',')}</strong>
 </div>
 </div>
 </div>
)}
</div>
);
}